import { useCallback, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';
import type { AuthUser } from '@driverforce/shared-types';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  login: () => void;
  logout: () => void;
  token: string | null;
}

interface JwtPayload {
  sub?: string;
  email?: string;
  realm_access?: { roles?: string[] };
}

const parseJwtPayload = (jwt: string): JwtPayload | null => {
  try {
    const [, payload] = jwt.split('.');
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
};

interface ProfileClaims {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

const toAuthUser = (user: User | null | undefined): AuthUser | null => {
  if (!user?.access_token) return null;
  const accessClaims = parseJwtPayload(user.access_token);
  const profile = (user.profile ?? {}) as ProfileClaims;
  const firstName = profile.given_name ?? null;
  const lastName = profile.family_name ?? null;
  const composedName =
    profile.name ?? ([firstName, lastName].filter(Boolean).join(' ').trim() || null);
  return {
    id: (accessClaims?.sub ?? profile.sub ?? '') as string,
    email: (accessClaims?.email ?? profile.email ?? '') as string,
    roles: (accessClaims?.realm_access?.roles ?? []) as string[],
    name: composedName && composedName.length > 0 ? composedName : null,
    firstName,
    lastName,
    picture: profile.picture ?? null,
  };
};

export const useAuth = (): AuthContextType => {
  const oidc = useOidcAuth();

  const authUser = useMemo(() => toAuthUser(oidc.user), [oidc.user]);
  const token = oidc.user?.access_token ?? null;

  const hasRole = useCallback(
    (role: string) => authUser?.roles.includes(role) ?? false,
    [authUser],
  );

  const login = useCallback(() => {
    void oidc.signinRedirect();
  }, [oidc]);

  const logout = useCallback(() => {
    const idToken = oidc.user?.id_token;
    const endSession = oidc.settings.metadata?.end_session_endpoint;
    if (endSession && idToken) {
      const url = `${endSession}?id_token_hint=${encodeURIComponent(idToken)}`;
      void fetch(url, { method: 'GET', mode: 'no-cors', credentials: 'include' }).catch(
        () => undefined,
      );
    }
    void oidc.removeUser();
  }, [oidc]);

  return {
    user: authUser,
    isAuthenticated: !!authUser && !oidc.isLoading,
    isLoading: oidc.isLoading,
    hasRole,
    login,
    logout,
    token,
  };
};
